"use client"

import { createContext, useContext, useMemo, type PropsWithChildren } from "react"
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
  useQueryStates,
} from "next-usequerystate"

import type { UnlikelyCollection } from "@unlikelystudio/commerce-connector"

import type { InferReturn, Nullish } from "~/@types/generics"
import {
  SHOPIFY_FILTERS_SORT_BY_KEYS,
  SORT_BY_PARAM,
  type SHOPIFY_FILTERS_SORT_KEYS,
} from "~/lib/shopify/constants/collection-filters"
import type { ShopifyProductFilters } from "~/lib/shopify/types"
import { facetIdToFacetName } from "~/lib/shopify/utils/filter-facets"
import type { serializeCollectionDefault } from "~/components/ui/Collection/CollectionDefault/_data/serializer"
import { FiltersAccordionStateProvider } from "~/providers/SearchAndDiscoveryProvider/FiltersAccordionStateProvider"
import {
  getDefaultFiltersNames,
  productFiltersToFacets,
} from "~/providers/SearchAndDiscoveryProvider/utils/product-filters-to-facets"
import { objectEntries } from "~/utils/object-entries"
import { objectValues } from "~/utils/object-values"
import { safeJSONParse } from "~/utils/safe-json-parse"

type SearchAndDiscoveryContextProps = {
  defaultFilters: NonNullable<ShopifyProductFilters>
  filters: UnlikelyCollection["filters"]
  initialPage: number
} & Pick<InferReturn<typeof serializeCollectionDefault>, "colorsWithThumbnail">

export type SearchAndDiscoveryContextState = Pick<SearchAndDiscoveryContextProps, "colorsWithThumbnail"> & {
  defaultFilters: ShopifyProductFilters
  facets: InferReturn<typeof productFiltersToFacets>
  isFiltering: boolean
  sortBy: SHOPIFY_FILTERS_SORT_KEYS
  setSortBy: (sortBy: SHOPIFY_FILTERS_SORT_KEYS) => void
  filters: Record<string, string[]>
  setFilters: (filters: Record<string, string[]>) => void
  page: number
  setPage: (page: number) => void
  query: Nullish<string>
  setQuery: (queryString: string) => void
  productFilters: ShopifyProductFilters
  reset: () => void
  filtersCount: number
}

const SearchAndDiscoveryContext = createContext<SearchAndDiscoveryContextState>({
  colorsWithThumbnail: [],
  defaultFilters: [],
  facets: [],
  isFiltering: false,
  sortBy: "default",
  setSortBy: () => null,
  filters: {},
  setFilters: () => null,
  page: 1,
  setPage: () => null,
  query: null,
  setQuery: () => null,
  productFilters: [],
  reset: () => null,
  filtersCount: 0,
})

export function useSearchAndDiscoveryContext() {
  return useContext(SearchAndDiscoveryContext)
}

export function SearchAndDiscoveryProvider({
  children,
  defaultFilters,
  filters: initialFacets,
  colorsWithThumbnail,
}: PropsWithChildren<SearchAndDiscoveryContextProps>) {
  const [sortBy, setSortBy] = useQueryState(
    SORT_BY_PARAM,
    parseAsStringLiteral(SHOPIFY_FILTERS_SORT_BY_KEYS).withDefault("default").withOptions({ clearOnDefault: true })
  )

  const [page, setPage] = useQueryState(
    "page",
    parseAsInteger.withDefault(1).withOptions({ clearOnDefault: true, shallow: false, history: "push", scroll: false })
  )

  const [query, setQuery] = useQueryState("q", parseAsString.withDefault("").withOptions({ clearOnDefault: true }))

  const defaultFiltersNames = getDefaultFiltersNames({ defaultFilters })

  const facets = productFiltersToFacets({ defaultFilters, filters: initialFacets })

  const [filters, setFilters] = useQueryStates(
    (facets ?? [])?.reduce((acc, facet) => {
      if (!facet?.id) {
        return acc
      }

      const name = facetIdToFacetName(facet)

      if (!name || !facet?.values?.length || defaultFiltersNames?.includes(name)) {
        return acc
      }

      return {
        ...acc,
        [name]: parseAsArrayOf(parseAsString)
          .withDefault([])
          .withOptions({ clearOnDefault: true, shallow: true, history: "replace" }),
      }
    }, {}),
    {
      history: "push",
      shallow: true,
    }
  )

  const resetFilters = () => {
    setFilters(objectEntries(filters as Record<string, string[]>).reduce((acc, [key]) => ({ ...acc, [key]: [] }), {}))
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const reset = () => {
    resetFilters()
    setSortBy("default")
    setPage(1)
    scrollToTop()
  }

  const value = useMemo(() => {
    const productFilters =
      objectEntries(filters as Record<string, string[]>)?.reduce<NonNullable<ShopifyProductFilters>>((acc, entry) => {
        const [key, valuesFromURLParams] = entry

        const relevantFacet = facets?.find((facet) => facet?.processedName === key)
        const { id, values } = relevantFacet ?? {}

        if (!id) {
          return acc
        }

        return [
          ...acc,
          ...(valuesFromURLParams
            ?.map((value) => {
              const stringifiedInput = values?.find((entry) => entry?.label === value)?.input?.toString() ?? "{}"

              return safeJSONParse<NonNullable<ShopifyProductFilters>[number]>(stringifiedInput)
            })
            ?.filter(Boolean) ?? []),
        ]
      }, defaultFilters ?? []) ?? []

    const filtersCount = objectValues(filters as Record<string, string[]>).reduce((acc, entry) => acc + entry.length, 0)

    return {
      isFiltering: objectValues(filters as Record<string, string[]>)?.some((entry) => entry?.length > 0),
      colorsWithThumbnail,
      defaultFilters,
      facets,
      sortBy,
      setSortBy,
      filters,
      setFilters,
      page,
      setPage,
      query,
      setQuery,
      productFilters,
      reset,
      filtersCount,
    }
  }, [colorsWithThumbnail, defaultFilters, facets, filters, page, query])

  return (
    <FiltersAccordionStateProvider>
      <SearchAndDiscoveryContext.Provider value={value}>{children}</SearchAndDiscoveryContext.Provider>
    </FiltersAccordionStateProvider>
  )
}
